import { Matrix, Resource, TextStyle, Texture } from "pixi.js";
import { Graphics, Sprite, Text } from "@pixi/react";
import EvStationSvg from "./Sprite/electricCharging.svg";
import { ParkPlaceIcon } from "./Sprite/SpriteIndex";
import BoundingBox from "../../Domain/Types/BoundingBox.type";

export const GeneParkingPlaceIcon = ({
  useParkIconText,
  isSelectable,
  circleRadius,
  placeScale,
  placeTypeId,
  boundingBox
}: {
  useParkIconText: boolean;
  isSelectable: boolean;
  circleRadius: number;
  placeScale: number;
  placeTypeId: number;
  boundingBox: BoundingBox;
}) => {
  const posX = 0 - circleRadius / 1.6;
  const posY = 0 - (boundingBox.height * placeScale) / 4 - circleRadius;

  // electric charging place
  if (placeTypeId === 4) {
    return (
      <Sprite
        data-testid="place-icon-electric-charging"
        image={EvStationSvg}
        scale={circleRadius * 0.005}
        x={0 - circleRadius / 1.8}
        y={0 - (boundingBox.height * placeScale) / 4 - circleRadius / 1.6}
        cursor={isSelectable ? "pointer" : "auto"}
      />
    );
  }

  // parking place // if (placeTypeId === 2) {
  else {
    // because of the performance issue, not use the Text object
    if (useParkIconText) {
      return (
        <Text
          data-testid="place-icon-text-parkingplace"
          text={"P"}
          style={new TextStyle({ fill: 0xffffff })}
          resolution={3}
          cursor={isSelectable ? "pointer" : "auto"}
          width={circleRadius * 1.3}
          height={circleRadius * 2}
          position={{ x: posX, y: posY }}
        />
      );
    }

    // because of the performance issue, use the Sprite object
    return (
      <Sprite
        data-testid="place-icon-spr-parkingplace"
        image={ParkPlaceIcon}
        cursor={isSelectable ? "pointer" : "auto"}
        width={circleRadius * 1.3}
        height={circleRadius * 2}
        position={{ x: posX, y: posY }}
      />
    );
  }
};

export const GeneIconImageGrp = ({
  iconImage,
  circleRadius,
  rotation,
  placeTypeId,
  placeScale,
  boundingBox,
  isSelectable
}: {
  iconImage: Texture<Resource>;
  circleRadius: number;
  rotation: number;
  placeTypeId: number;
  placeScale: number;
  boundingBox: BoundingBox;
  isSelectable: boolean;
}) => {
  return (
    <Graphics
      data-testid="place-icon-grp-user-img"
      draw={g => {
        const mtx = new Matrix();

        const scaleX = (circleRadius * 2) / iconImage.width;
        const scaleY = (circleRadius * 2) / iconImage.height;

        const transX = 0 - (circleRadius * 2) / 2;
        const transY = 0 - (circleRadius * 2) / 2;

        mtx.scale(scaleX, scaleY);
        mtx.translate(transX, transY);
        const radians = 0 - (rotation * Math.PI) / 180;
        /** always show profile pic at 0 degree */
        mtx.rotate(radians);

        g.clear();
        g.beginFill(0xffffff);
        g.drawCircle(0, 0, circleRadius);
        g.endFill();
        g.beginTextureFill({ texture: iconImage, matrix: mtx });
        g.drawCircle(0, 0, circleRadius);
        g.endFill();
      }}
      x={0}
      y={placeTypeId === 1 ? 0 - 16 * placeScale : 0 - (boundingBox.height * placeScale) / 4}
      cursor={isSelectable ? "pointer" : "auto"}
    />
  );
};

const getTextStyle = (isColleaguesPlace: boolean | undefined, fontSize?: number) =>
  new TextStyle({
    fill: isColleaguesPlace ? 0xffffff : 0x000000,
    align: "center",
    ...(fontSize && { fontSize })
  });

const getCommonTextProps = (
  isSelectable: boolean,
  circleRadius: number,
  rotation: number,
  isColleaguesPlace?: boolean
) => ({
  resolution: isColleaguesPlace ? 10 : 20,
  cursor: isSelectable ? "pointer" : "auto",
  width: circleRadius,
  height: circleRadius,
  anchor: 0.5, // center alignment
  rotation: -rotation * (Math.PI / 180), // always upright text
  position: { x: 0, y: 0 }
});

export const InitialText = ({
  text,
  position,
  style,
  rotation,
  ...props
}: {
  text: string;
  position: { x: number; y: number };
  style: TextStyle;
  rotation?: number;
  [key: string]: any;
}) => <Text text={text} style={style} rotation={rotation} position={position} {...props} />;

export const WPInitialText = ({
  icon,
  circleRadius,
  posY,
  rotation,
  isColleaguesPlace,
  isSelectable
}: {
  icon: string;
  circleRadius: number;
  posY: number;
  rotation: number;
  isColleaguesPlace: boolean | undefined;
  isSelectable: boolean;
  initialPosY: number;
}) => {
  // if icon string shows the initial only
  if (icon.length === 2) {
    return (
      <InitialText
        data-testid="place-icon-text-initial-users"
        text={icon.toUpperCase()}
        x={0}
        y={posY}
        style={getTextStyle(isColleaguesPlace, circleRadius * 2)}
        {...getCommonTextProps(isSelectable, circleRadius * 1.3, rotation, isColleaguesPlace)}
      />
    );
  }

  // if icon string shows has number of other booking 'FR+2'
  if (icon.length >= 4 && icon.length <= 6) {
    return (
      <>
        <InitialText
          data-testid="place-icon-text-initial-users-upper"
          text={icon.substring(0, 2).toUpperCase()}
          x={0 + circleRadius / 4}
          y={posY + circleRadius / 5}
          style={getTextStyle(isColleaguesPlace, circleRadius * 1.1)}
          {...getCommonTextProps(isSelectable, circleRadius * 1.1, rotation, isColleaguesPlace)}
        />
        <InitialText
          data-testid="place-icon-text-initial-users-bottom"
          text={icon.substring(2).toUpperCase()}
          x={-circleRadius / 2.75}
          y={posY - circleRadius / 3.5}
          style={getTextStyle(isColleaguesPlace, circleRadius * 0.8)}
          {...getCommonTextProps(isSelectable, circleRadius * 0.8, rotation, isColleaguesPlace)}
        />
      </>
    );
  }
};
